import React, { FC, ChangeEvent, ReactElement, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

import SettingsCard from 'components/@common/Cards/SettingsCard';
import {
  WdtSettingCharacterOrderData,
  WdtCharacterOrder,
  Classroom
} from 'types/schema-generated-types';
import GET_WDT_CLASSROOM_SETTINGS from 'graph/settings/wdt/get-wdt-classroom-settings-query';
import UPDATE_WDT_CLASSROOM_CHARACTER_ORDER_SETTING from 'graph/settings/wdt/update-wdt-classroom-character-order-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface LanguageOptionsInputs {
  label: string;
  value: string;
}

interface LetterNumberOrderProps {
  classroom: Classroom;
  setting: WdtSettingCharacterOrderData | null;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2)
    }
  };
});

const letterOptions: LanguageOptionsInputs[] = [
  {
    label: 'HWT Order (Letters in HWT order and numbers in numerical order)',
    value: WdtCharacterOrder.hwt
  },
  {
    label:
      'Alphabetical/Numerical Order (Letters in alphabetical order and numbers in numerical order)',
    value: WdtCharacterOrder.natural
  },
  { label: 'Free Play (All letters and numbers unlocked)', value: WdtCharacterOrder.freePlay }
];

const getClassroomWdtSettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_WDT_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateWdtClassroomSettingCharacterOrder }
      ): { wdtClassroomSettings: any } => {
        const [{ value }] = updateWdtClassroomSettingCharacterOrder;
        return {
          wdtClassroomSettings: {
            ...cachedData?.wdtClassroomSettings,
            characterOrder: value
          }
        };
      }
    }
  ])
});

const LetterNumberOrder: FC<LetterNumberOrderProps> = ({
  classroom,
  expanded,
  setting: settingValue,
  handleChangeExpanded
}) => {
  const { t } = useTranslation('LetterNumberOrderSetting');
  const classes = useStyles();

  const { id: classroomId } = classroom;
  const [, { openAlert }] = useStore();
  const [updateWdtClassroomSettingCharacterOrder] = useMutation(
    UPDATE_WDT_CLASSROOM_CHARACTER_ORDER_SETTING,
    getClassroomWdtSettingsMutationCacheUpdate(+classroomId)
  );

  const [value, setValue] = useState(settingValue?.lowercase || WdtCharacterOrder.hwt);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue((event.target as HTMLInputElement).value as WdtCharacterOrder);
  };

  const handleOnCancel = (): void => setValue(settingValue?.lowercase || WdtCharacterOrder.hwt);

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateWdtClassroomSettingCharacterOrder({
        variables: {
          data: {
            classroomId,
            value: {
              numbers: value,
              capitals: value,
              lowercase: value
            }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Letter and Number Order', t));
    } catch (error) {
      // TODO
      console.log('ERROR');
    }
  };

  return (
    <SettingsCard
      title={t('Letter and Number Order')}
      panel="letterNumberOrder"
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={t(
        'Choose the letter and number order students will use (Default is HWT order).'
      )}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <RadioGroup
          aria-label="letter number setting"
          name="letterNumberSetting"
          value={value}
          onChange={handleChange}
        >
          {letterOptions.map(
            ({ label, value }, key): ReactElement => (
              <FormControlLabel
                key={`letter-number-option-${key}`}
                value={value}
                control={<Radio />}
                label={label}
              />
            )
          )}
        </RadioGroup>
      </FormControl>
    </SettingsCard>
  );
};

export default LetterNumberOrder;
